import { CircleCheck, CircleAlert, Clock, Download } from 'lucide-react';
import { ColumnDef } from '@tanstack/react-table';
import { parse } from 'date-fns';

import { BuslineImportDto } from '@area-restrita/api-client/model/buslineImportDto';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import Dialog from '@/components/template/Dialog';
import EntriesTableDialog from './EntriesTableDialog';

export const columns: ColumnDef<BuslineImportDto>[] = [
  { id: 'id', header: 'ID', accessorKey: 'id' },
  {
    id: 'scheduledDate',
    header: 'Data Agendada',
    accessorFn: ({ scheduledDate }) =>
      parse(scheduledDate, 'yyyy-MM-dd', new Date()).toLocaleDateString(
        'pt-BR',
      ),
  },

  {
    accessorKey: 'invalidCount',
    header: 'Dados invalidos',
    cell: ({ row }) => (
      <Dialog
        title="Dados Inválidos"
        description="Linhas com dados inválidos não serão importadas. Faça o download do arquivo para corrigir os erros e tente novamente."
        className="sm:max-w-7xl w-full"
        tooltipContent={
          row.original.invalidCount === 0
            ? 'Nenhum dado inválido'
            : 'Ver dados inválidos'
        }
        trigger={
          <Button
            size="icon-sm"
            variant="destructive"
            disabled={row.original.invalidCount === 0}
          >
            <Download className="text-white" strokeWidth="2px" />
          </Button>
        }
      >
        <EntriesTableDialog buslineImport={row.original} status="invalid" />
      </Dialog>
    ),
  },
  {
    accessorKey: 'validCount',
    header: 'Dados validos',
    cell: ({ row }) => (
      <Dialog
        title="Dados Válidos"
        description="Visualize os dados válidos que serão importados para o sistema."
        className="sm:max-w-7xl w-full"
        tooltipContent={
          row.original.validCount === 0
            ? 'Nenhum dado válido'
            : 'Ver dados válidos'
        }
        trigger={
          <Button
            size="icon-sm"
            variant="outline"
            disabled={row.original.validCount === 0}
          >
            <Download className="text-black" strokeWidth="2px" />
          </Button>
        }
      >
        <EntriesTableDialog buslineImport={row.original} status="valid" />
      </Dialog>
    ),
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => {
      if (row.original.status === 'failed') {
        return (
          <Dialog
            title="Erro na Importação"
            className="sm:max-w-5xl w-full"
            tooltipContent="Ver detalhes do erro"
            trigger={
              <button>
                <Badge
                  variant="destructive"
                  className="px-1.5 gap-1 cursor-pointer"
                >
                  <CircleAlert className="size-4" />
                  Falhou
                </Badge>
              </button>
            }
          >
            <pre className="whitespace-pre-wrap break-words bg-muted p-4 rounded-md border border-border">
              {row.original.errorLog
                ? JSON.stringify(JSON.parse(row.original.errorLog), null, 2)
                : row.original.errorLog}
            </pre>
          </Dialog>
        );
      }

      return (
        <Badge variant="outline" className="text-muted-foreground px-1.5 gap-1">
          {{
            executed: <CircleCheck className="w-4 h-4 stroke-green-500" />,
            pending: <Clock className="w-4 h-4" />,
            failed: <CircleAlert className="w-4 h-4" />,
          }[row.original.status] || null}

          {{
            executed: 'Executado',
            pending: 'Agendado',
            failed: 'Falhou',
          }[row.original.status] || row.original.status}
        </Badge>
      );
    },
  },
];
